import { FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { Box, Button, Grid, Stack } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { ModalLayout, CardAttribute, AuthorizationGuard } from '@/components'
import { Indicator } from '@/components/ui'
import { ControlledDateField, ControlledTextField } from '@/components/inputs'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import dayjs, { Dayjs } from 'dayjs'
import { Edit, XSquare } from 'react-feather'
import {
  useAuth,
  useMutationWrapper,
  usePostManualHoursReport,
  usePatchManualHoursReport,
  usePostManualHoursReportApprove,
  usePostManualHoursReportDecline,
  usePostManualHoursReportDisapprove,
  useModal,
} from '@/hooks'
import {
  ManualHoursReport as ManualHoursReportType,
  ManualHoursHrReport,
  ManualHoursReportStatuses,
  UserGroups,
  PostManualHoursReport,
} from '@/types'
import {
  DATE_FORMAT_YMD,
  DATE_FORMAT_DMY_TIME,
  DATE_FORMAT_DMY,
} from '@/constants'

export type ManualHoursReportForm = {
  date: Dayjs
  amount_minutes: number | string
  description: string
}

export type ManualHoursReportStatusForm = {
  validation_message: string
}

export type ManualHoursReportProps = {
  reportData?: ManualHoursReportType | ManualHoursHrReport
}

export const ManualHoursReport = ({ reportData }: ManualHoursReportProps) => {
  const { t } = useTranslation('manualHoursReporting')
  const { close } = useModal()

  const { hasPermission } = useAuth()
  const isEmployee = hasPermission([
    UserGroups.EMPLOYEE,
    UserGroups.MANAGER,
    UserGroups.SOUND_TECHNICIAN,
    UserGroups.DATA_CONTROLLER,
  ])

  const canEditExisting = isEmployee
  const isCreate = !reportData

  const [data, setData] = useState<
    (ManualHoursReportType | ManualHoursHrReport) | undefined
  >(reportData)

  const [isEdit, setIsEdit] = useState<boolean>(!reportData)

  const schema = yup.object({
    date: yup.mixed<Dayjs>().required(),
    amount_minutes: yup
      .number()
      .transform((v, o) => (o === '' || o === null ? null : v))
      .integer(t('errors.invalidInteger'))
      .min(0, t('errors.mustBeNonNegative'))
      .required()
      .nullable(true),
    description: yup.string().required(),
  })

  const defaultValues: ManualHoursReportForm = {
    date: reportData?.date ? dayjs(reportData.date) : dayjs(),
    amount_minutes: reportData?.amount_minutes ?? '',
    description: reportData?.description ?? '',
  }

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
  } = useForm<ManualHoursReportForm>({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  })

  const {
    control: statusControl,
    handleSubmit: handleStatusSubmit,
    formState: { isDirty: isStatusDirty },
  } = useForm<ManualHoursReportStatusForm>({
    mode: 'onChange',
    defaultValues: { validation_message: '' },
    resolver: yupResolver(
      yup.object({ validation_message: yup.string().required() })
    ),
  })

  const { mutateAsync: createItem, isLoading: creating } = useMutationWrapper(
    () => usePostManualHoursReport()
  )
  const { mutateAsync: updateItem, isLoading: updating } = useMutationWrapper(
    () => usePatchManualHoursReport(reportData?.id as number)
  )
  const { mutateAsync: approveItem, isLoading: approving } = useMutationWrapper(
    () => usePostManualHoursReportApprove({ id: reportData?.id as number })
  )
  const { mutateAsync: declineItem, isLoading: declining } = useMutationWrapper(
    () => usePostManualHoursReportDecline({ id: reportData?.id as number })
  )
  const { mutateAsync: disapproveItem, isLoading: disapproving } =
    useMutationWrapper(() =>
      usePostManualHoursReportDisapprove({ id: reportData?.id as number })
    )

  const isSaving = creating || updating

  const onSubmit = async (form: ManualHoursReportForm) => {
    const payload = {
      amount_minutes: Number(form.amount_minutes),
      date: form.date.format(DATE_FORMAT_YMD),
      description: form.description || null,
    } as PostManualHoursReport

    if (isCreate) {
      await createItem(payload, {
        successMessageKey: 'manualHoursReporting:notifications.saved',
        onSuccess: () => {
          close()
        },
      })
    } else if (canEditExisting && data) {
      await updateItem(payload, {
        successMessageKey: 'manualHoursReporting:notifications.updated',
        onSuccess: (res) => {
          setData(res)
          setIsEdit(false)
        },
      })
    }
  }

  const handleApprove = async (form: ManualHoursReportStatusForm) => {
    await approveItem(
      { validation_message: form.validation_message },
      {
        successMessageKey: 'manualHoursReporting:notifications.approved',
        onSuccess: (res) => {
          setData(res)
        },
      }
    )
  }

  const handleDecline = async (form: ManualHoursReportStatusForm) => {
    await declineItem(
      { validation_message: form.validation_message },
      {
        successMessageKey: 'manualHoursReporting:notifications.declined',
        onSuccess: (res) => {
          setData(res)
        },
      }
    )
  }

  const handleDisapprove = async () => {
    await disapproveItem(undefined, {
      successMessageKey: 'manualHoursReporting:notifications.disapproved',
      onSuccess: (res) => {
        setData(res)
      },
    })
  }

  const onSubmitStatusForm = (e: FormEvent) => {
    const action = (
      e.nativeEvent as SubmitEvent
    ).submitter?.attributes.getNamedItem('name')?.value
    const handler = action === 'approve' ? handleApprove : handleDecline

    handleStatusSubmit(async (form) => handler(form))(e)
  }

  const title = isCreate ? t('createTitle') : t('detailTitle', { id: data?.id })

  return (
    <ModalLayout title={title}>
      <Stack spacing={4}>
        {!!data && (
          <Grid container={true} spacing={2}>
            {(data as ManualHoursHrReport).user && (
              <Grid item={true} xs={12} md={6}>
                <CardAttribute
                  title={t('fieldTitles.user')}
                  value={
                    `${(data as ManualHoursHrReport).user.first_name ?? ''} ${
                      (data as ManualHoursHrReport).user.last_name ?? ''
                    }`.trim() || (data as ManualHoursHrReport).user.email
                  }
                />
              </Grid>
            )}
            <Grid item={true} xs={12} md={6}>
              <CardAttribute
                title={t('fieldTitles.created')}
                value={
                  data.created_at
                    ? dayjs(data.created_at).format(DATE_FORMAT_DMY_TIME)
                    : '-'
                }
              />
            </Grid>
            <Grid item={true} xs={12} md={6}>
              <CardAttribute
                title={t('fieldTitles.updated')}
                value={
                  data.updated_at
                    ? dayjs(data.updated_at).format(DATE_FORMAT_DMY_TIME)
                    : '-'
                }
              />
            </Grid>
            {!isEmployee && (
              <Grid item={true} xs={12} md={6}>
                <CardAttribute
                  title={t('fieldTitles.exported')}
                  value={
                    data.exported_at
                      ? dayjs(data.exported_at).format(DATE_FORMAT_DMY_TIME)
                      : '-'
                  }
                />
              </Grid>
            )}
          </Grid>
        )}

        {!isCreate && (
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={4}
            width="100%"
          >
            <span />
            <AuthorizationGuard
              allowedGroups={[UserGroups.EMPLOYEE]}
              redirect={false}
            >
              {canEditExisting && (
                <Button
                  onClick={() => setIsEdit((v) => !v)}
                  variant="textThin"
                  endIcon={isEdit ? <XSquare size={14} /> : <Edit size={14} />}
                >
                  {isEdit ? t('buttons.closeEditing') : t('buttons.edit')}
                </Button>
              )}
            </AuthorizationGuard>
          </Stack>
        )}

        {isEdit || isCreate ? (
          <>
            <Stack spacing={4}>
              <Stack direction="row" spacing={4}>
                <Box>
                  <ControlledDateField<ManualHoursReportForm>
                    name="date"
                    control={control}
                    placeholder={t('placeholders.date')}
                  />
                </Box>
                <Box>
                  <ControlledTextField<ManualHoursReportForm>
                    name="amount_minutes"
                    control={control}
                    type="number"
                    placeholder={t('placeholders.amountMinutes')}
                    inputProps={{ min: 2 }}
                  />
                </Box>
              </Stack>
              <ControlledTextField<ManualHoursReportForm>
                name="description"
                control={control}
                multiline={true}
                minRows={3}
                placeholder={t('placeholders.description')}
              />
            </Stack>

            <Stack direction="row" justifyContent="flex-end" spacing={2}>
              {(isCreate || canEditExisting) && (
                <LoadingButton
                  variant="contained"
                  onClick={handleSubmit(onSubmit)}
                  loading={isSaving}
                  disabled={!isValid || (!isCreate && !isDirty)}
                >
                  {isCreate ? t('buttons.create') : t('buttons.save')}
                </LoadingButton>
              )}
            </Stack>
          </>
        ) : (
          <Grid container={true} spacing={2}>
            <Grid item={true} xs={12} md={6}>
              <CardAttribute
                title={t('fieldTitles.date')}
                value={
                  (data?.date && dayjs(data.date).format(DATE_FORMAT_DMY)) ||
                  '-'
                }
              />
            </Grid>
            <Grid item={true} xs={12} md={6}>
              <CardAttribute
                title={t('fieldTitles.amountMinutes')}
                value={data?.amount_minutes ?? '-'}
              />
            </Grid>
            <Grid item={true} xs={12}>
              <CardAttribute
                title={t('fieldTitles.description')}
                value={data?.description || '-'}
              />
            </Grid>

            {!!data?.status && (
              <Grid item={true} xs={12}>
                <Stack direction="column" gap={2}>
                  <CardAttribute
                    title={t('fieldTitles.status')}
                    value={
                      <Indicator
                        isSmall={false}
                        status={data.status}
                        name={t(`status.${data.status}`)}
                      />
                    }
                  />
                  <AuthorizationGuard
                    allowedGroups={[UserGroups.HR]}
                    redirect={false}
                  >
                    {[
                      ManualHoursReportStatuses.APPROVED,
                      ManualHoursReportStatuses.DECLINED,
                    ].includes(data.status as ManualHoursReportStatuses) && (
                      <Box>
                        <LoadingButton
                          variant="contained"
                          color="warning"
                          type="button"
                          loading={disapproving}
                          disableElevation={true}
                          disabled={disapproving}
                          onClick={handleDisapprove}
                        >
                          <span>
                            {data.status === ManualHoursReportStatuses.APPROVED
                              ? t('buttons.disapprove')
                              : t('buttons.reopen')}
                          </span>
                        </LoadingButton>
                      </Box>
                    )}
                  </AuthorizationGuard>
                </Stack>
              </Grid>
            )}

            {!!data?.validation_message && (
              <Grid item={true} xs={12}>
                <CardAttribute
                  title={t('fieldTitles.validationMessage')}
                  value={data.validation_message}
                />
              </Grid>
            )}

            {!!data?.approved_by && (
              <Grid item={true} xs={12} md={6}>
                <CardAttribute
                  title={t('fieldTitles.approvedBy')}
                  value={
                    `${data.approved_by.first_name ?? ''} ${
                      data.approved_by.last_name ?? ''
                    }`.trim() || data.approved_by.email
                  }
                />
              </Grid>
            )}

            <AuthorizationGuard
              allowedGroups={[UserGroups.HR]}
              redirect={false}
            >
              {data?.status === ManualHoursReportStatuses.PENDING && (
                <Grid item={true} xs={12}>
                  <Box mt={2}>
                    <form onSubmit={onSubmitStatusForm}>
                      <Stack spacing={2} alignItems="flex-end">
                        <Box width="100%">
                          <ControlledTextField<ManualHoursReportStatusForm>
                            name="validation_message"
                            control={statusControl}
                            placeholder={t('placeholders.validationMessage')}
                            multiline={true}
                            rows={2}
                            inputProps={{
                              style: { padding: 0 },
                            }}
                          />
                        </Box>
                        <Stack direction="row" gap={4}>
                          <LoadingButton
                            variant="contained"
                            color="error"
                            type="submit"
                            name="decline"
                            loading={declining}
                            disableElevation={true}
                            disabled={!isStatusDirty || approving || declining}
                          >
                            <span>{t('buttons.decline')}</span>
                          </LoadingButton>
                          <LoadingButton
                            variant="contained"
                            color="secondary"
                            type="submit"
                            name="approve"
                            loading={approving}
                            disableElevation={true}
                            disabled={!isStatusDirty || approving || declining}
                          >
                            <span>{t('buttons.approve')}</span>
                          </LoadingButton>
                        </Stack>
                      </Stack>
                    </form>
                  </Box>
                </Grid>
              )}
            </AuthorizationGuard>
          </Grid>
        )}
      </Stack>
    </ModalLayout>
  )
}
